import { useMsal } from '@azure/msal-react'
import {
  faArrowRight,
  faEllipsis,
  faFlag,
  faPencil,
  faPlus,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Badge,
  Button,
  Group,
  Image,
  Menu,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { loginRequest } from '../authConfig'
import BusinessHoursComponent from '../components/BusinessHours'
import DeviceCard from '../components/location/DeviceCard'
import ReportLocationModal from '../components/location/ReportLocationModal'
import { useSessionInfo } from '../contexts/SessionContext'
import { fetchLocationById } from '../data/location.service'
import { DailyBusinessHours } from '../models/DailyBusinessHours'
import { Device } from '../models/Device'
import { Location } from '../models/Location'
import { COLORS, DAYSOFWEEK, HostOnboardingSteps } from '../utils/constants'
import { formatAddress } from '../utils/displayUtils'
import classes from './css/Location.module.css'

const footTrafficRanges = {
  low: '0–50',
  moderate: '51–150',
  steady: '151–300',
  busy: '301–600',
  high: '601+',
}

// Function to sort business hours in correct order
const sortBusinessHours = (
  businessHours: Record<string, DailyBusinessHours>,
) => {
  const sortedHours: Record<string, DailyBusinessHours> = {}

  // Always return in correct order: Monday through Sunday
  DAYSOFWEEK.forEach((day) => {
    if (businessHours[day]) {
      sortedHours[day] = businessHours[day]
    }
  })

  return sortedHours
}

const LocationPage = () => {
  const { locationId } = useParams()
  const [location, setLocation] = useState<Location>()
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const { session } = useSessionInfo()
  const { instance } = useMsal()
  const navigate = useNavigate()

  const handleBookingClick = (device: Device) => {
    if (session != null && instance) {
      if ((session.allLocationsBundleCount ?? 0) > 0) {
        notifications.show({
          title: 'All Locations bundle in cart',
          message:
            'You already have an All Locations bundle in your cart. Remove it before booking individual locations, or proceed to checkout with your bundle.',
          color: 'yellow',
          autoClose: 6000,
        })
        return
      }
      navigate(`/devices/${device.id}/configure-ad`)
    } else {
      instance.loginRedirect(loginRequest)
    }
  }

  const handleDeviceDeleted = async () => {
    if (locationId) {
      try {
        const updatedLocation = await fetchLocationById(locationId)
        // Sort business hours after fetching
        if (updatedLocation.businessHours) {
          updatedLocation.businessHours = sortBusinessHours(
            updatedLocation.businessHours,
          )
        }
        setLocation(updatedLocation)
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: 'Failed to refresh location data.',
          color: 'red',
        })
      }
    }
  }

  useEffect(() => {
    if (locationId) {
      fetchLocationById(locationId, session != null ? instance : undefined)
        .then((l) => {
          // Sort business hours after fetching
          if (l.businessHours) {
            l.businessHours = sortBusinessHours(l.businessHours)
          }
          setLocation(l)
        })
        .catch((ex: any) => {
          notifications.show({
            title: 'An unknown error occurred',
            message:
              'Unable to get location. Try again. If you are unable to resolve the issue, please contact us for help',
            autoClose: false,
          })
        })
    }
  }, [locationId, instance, session])

  const isDeviceComplete = (device: Device) => {
    return Boolean(
      device.name &&
        device.imageUrl &&
        device.approvalType &&
        device.screenSize &&
        device.monthlyPrice,
    )
  }

  const incompleteDevices =
    location?.devices.filter((device) => !isDeviceComplete(device)) || []
  const firstIncompleteDevice = incompleteDevices[0]

  return location != null ? (
    <Stack
      h={'100%'}
      w='100%'
      p={{ base: 'md', sm: 'xl', md: 'xl' }}
      px={{ base: 'md', sm: '5%' }}
      maw={1400}
      style={{ alignSelf: 'center' }}
    >
      <Stack gap='xs'>
        <Group align='center' gap='md'>
          <Title order={2} style={{ wordBreak: 'break-word' }}>
            {location.name}
          </Title>
          <Group gap='xs'>
            <ActionIcon
              bg={COLORS.LightGray}
              style={{
                borderRadius: 12,
                padding: 8,
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => setReportModalOpen(true)}
            >
              <FontAwesomeIcon icon={faFlag} size='sm' color='black' />
            </ActionIcon>
            {session?.organizationId === location.organizationId && (
              <Menu shadow='md' width={200}>
                <Menu.Target>
                  <ActionIcon
                    bg={COLORS.LightGray}
                    style={{
                      borderRadius: 12,
                      padding: 8,
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faEllipsis}
                      size='lg'
                      color='black'
                    />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<FontAwesomeIcon icon={faPencil} />}
                    onClick={() => navigate(`/locations/edit/${location.id}`)}
                  >
                    Edit Location
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>

        {location.categories && location.categories.length > 0 && (
          <Group gap='xs' wrap='wrap'>
            {location.categories.map((category, index) => (
              <Badge
                key={index}
                variant='light'
                color={COLORS.LightGreen}
                size='sm'
              >
                {category}
              </Badge>
            ))}
          </Group>
        )}
      </Stack>

      <Group align='flex-start' gap='xl'>
        <Stack flex='2 2 450px'>
          <AspectRatio ratio={4 / 3}>
            <Image src={location.imageUrls[0]} className={classes.image} />
          </AspectRatio>
          {location.footTraffic && (
            <Stack gap={8} mt='md'>
              <Badge
                variant='light'
                color={COLORS.LightGreen}
                size='sm'
                style={{ alignSelf: 'flex-start' }}
              >
                {location.footTraffic.charAt(0).toUpperCase() +
                  location.footTraffic.slice(1)}{' '}
                Foot Traffic (
                {footTrafficRanges[
                  location.footTraffic as keyof typeof footTrafficRanges
                ] || ''}
                )
              </Badge>
            </Stack>
          )}

          <Stack gap={0} mt={10}>
            <Text size='md'>{location.description}</Text>
          </Stack>

          <Stack gap={0}>
            <Text className={classes.labelText}>Address</Text>
            {formatAddress(location).map((line, index) => (
              <Text key={index} size='md'>
                {line}
              </Text>
            ))}
          </Stack>
          <Stack gap={8} mt='lg'>
            <Text className={classes.labelText}>Business Hours</Text>
            <Paper p='lg' radius='md' withBorder>
              <BusinessHoursComponent businessHours={location.businessHours} />
            </Paper>
          </Stack>
        </Stack>

        <Stack flex='1 1 250px'>
          {session?.organizationId === location.organizationId && (
            <UnstyledButton
              onClick={() =>
                navigate(`/locations/${locationId}/devices/add`, {
                  state: {
                    location,
                    device: null,
                    isHostOnboarding: false,
                    currentStep: HostOnboardingSteps.DeviceSetupInstructions,
                  },
                })
              }
              style={{
                color: '#136139',
                borderRadius: 4,
                transition: 'color 150ms ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#13a139')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#136139')}
              w='100%'
            >
              <Paper shadow='md' radius='lg' p='sm' withBorder>
                <Group justify='center'>
                  <Group gap='xs' align='center'>
                    <Text fw={500} size='md'>
                      Add New Device
                    </Text>
                    <FontAwesomeIcon icon={faPlus} />
                  </Group>
                </Group>
              </Paper>
            </UnstyledButton>
          )}

          {firstIncompleteDevice && (
            <Paper
              shadow='md'
              radius='lg'
              p='md'
              withBorder
              w='100%'
              style={{
                backgroundColor: '#f8f9fa',
              }}
            >
              <Stack gap='xs'>
                <Group justify='space-between' align='center'>
                  <Text fw={500}>
                    {firstIncompleteDevice.name || 'Unnamed Device'}
                  </Text>
                  <Badge color={COLORS.HiddenGemzRed} size='sm'>
                    Incomplete
                  </Badge>
                </Group>
                <Text size='sm' c='dimmed'>
                  Complete device details to start accepting advertisements
                </Text>
                <Button
                  variant='light'
                  rightSection={<FontAwesomeIcon icon={faArrowRight} />}
                  onClick={() =>
                    navigate(`/devices/edit/${firstIncompleteDevice.id}`, {
                      state: {
                        location,
                        device: firstIncompleteDevice,
                        isHostOnboarding: false,
                        currentStep: HostOnboardingSteps.DevicePhotos,
                      },
                    })
                  }
                  size='xs'
                >
                  Complete Device Details
                </Button>
              </Stack>
            </Paper>
          )}

          {location.devices.length === 0 ? (
            <Paper shadow='md' radius='lg' p='xl' withBorder w='100%'>
              <Stack align='center' gap='md'>
                <Text fw={500} size='lg' ta='center'>
                  No devices configured for this location
                </Text>
              </Stack>
            </Paper>
          ) : (
            location.devices.map((device) => (
              <DeviceCard
                key={device.id}
                device={device}
                location={location}
                organizationId={location.organizationId}
                onBookingClick={() => handleBookingClick(device)}
                onDeviceDeleted={handleDeviceDeleted}
                isIncomplete={!isDeviceComplete(device)}
              />
            ))
          )}
        </Stack>
      </Group>

      <ReportLocationModal
        opened={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        locationId={location?.id ?? 0}
        instance={instance}
      />
    </Stack>
  ) : (
    <Stack
      h={'100%'}
      w='100%'
      p={{ base: 'md', sm: 'xl', md: 'xl' }}
      px={{ base: 'md', sm: '5%' }}
      maw={1400}
      style={{ alignSelf: 'center' }}
    >
      <Skeleton w={{ base: '60%', sm: '30%' }} h={48} radius='lg' />
      <Group align='flex-start' gap={50} display={{ base: 'none', sm: 'flex' }}>
        <Stack w='63%' h='100%'>
          <Skeleton h={600} radius='lg' />
          <Skeleton h={100} radius='lg' />
          <Skeleton h={200} radius='lg' />
        </Stack>
        <Skeleton w='33%' h={450} radius='lg' />
      </Group>
      <Stack gap='xl' display={{ base: 'flex', sm: 'none' }}>
        <Stack w='100%'>
          <Skeleton h={300} radius='lg' />
          <Skeleton h={60} radius='lg' />
          <Skeleton h={120} radius='lg' />
        </Stack>
        <Skeleton w='100%' h={300} radius='lg' />
      </Stack>
    </Stack>
  )
}

export default LocationPage
